import { useRef } from 'react';
import useAutomationsStore from '../../../../../store/useAutomationsStore';
import VariablePicker from './VariablePicker';

export default function ConfigField({ field, value, onChange, allData, onChangeField }) {
  const { nodes, tokens, networks } = useAutomationsStore();
  const inputRef = useRef(null);

  const baseClass =
    'w-full px-3 py-2 text-sm border border-gray-200 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-blue-200 focus:border-blue-400';

  const supportsVariables = ['text', 'textarea', 'password'].includes(field.type);

  const handleInsertVariable = (variable) => {
    const el = inputRef.current;
    if (!el) {
      onChange((value || '') + variable);
      return;
    }
    const start = el.selectionStart ?? (value || '').length;
    const end = el.selectionEnd ?? start;
    const current = value || '';
    const updated = current.slice(0, start) + variable + current.slice(end);
    onChange(updated);
    requestAnimationFrame(() => {
      const pos = start + variable.length;
      el.focus();
      el.setSelectionRange(pos, pos);
    });
  };

  // Find sibling token/network field keys from allData
  const findSiblingKey = (type) => {
    if (!allData) return null;
    // Common pairings: network+token, network+contractAddress
    const tokenKeys = ['token', 'contractAddress'];
    const networkKeys = ['network'];
    if (type === 'token') return tokenKeys.find((k) => k in allData) || null;
    if (type === 'network') return networkKeys.find((k) => k in allData) || null;
    return null;
  };

  if (field.type === 'wallet_select') {
    const walletNodes = nodes.filter((n) => n.data?.nodeType === 'wallet_load');
    return (
      <select value={value || ''} onChange={(e) => onChange(e.target.value)} className={baseClass}>
        <option value="">{field.placeholder || 'Select wallet'}</option>
        {walletNodes.map((wn) => (
          <option key={wn.id} value={wn.id}>
            {wn.data.walletLabel || `Wallet (${wn.id.slice(0, 6)})`}
          </option>
        ))}
      </select>
    );
  }

  if (field.type === 'network_select') {
    const handleNetworkChange = (networkId) => {
      onChange(networkId);
      if (!onChangeField) return;

      // Only clear token if a specific network is selected and the token belongs to a different one
      const tokenKey = findSiblingKey('token');
      if (tokenKey && allData?.[tokenKey] && networkId) {
        const selectedToken = tokens.find((t) => t.id === allData[tokenKey]);
        if (selectedToken && selectedToken.network?.id !== networkId) {
          onChangeField(tokenKey, '');
        }
      }
    };

    return (
      <select value={value || ''} onChange={(e) => handleNetworkChange(e.target.value)} className={baseClass}>
        <option value="">Any network</option>
        {networks.map((n) => (
          <option key={n.id} value={n.id}>
            {n.name}
          </option>
        ))}
      </select>
    );
  }

  if (field.type === 'token_select') {
    // Filter tokens by selected network if one is set
    const networkKey = findSiblingKey('network');
    const selectedNetworkId = networkKey ? allData?.[networkKey] : null;
    const filteredTokens = selectedNetworkId
      ? tokens.filter((t) => t.network?.id === selectedNetworkId)
      : tokens;

    const handleTokenChange = (tokenId) => {
      onChange(tokenId);
      if (!onChangeField || !tokenId) return;

      // Auto-select network from token
      const token = tokens.find((t) => t.id === tokenId);
      if (token?.network?.id && networkKey) {
        onChangeField(networkKey, token.network.id);
      }
    };

    return (
      <select value={value || ''} onChange={(e) => handleTokenChange(e.target.value)} className={baseClass}>
        <option value="">Any token</option>
        {filteredTokens.map((t) => (
          <option key={t.id} value={t.id}>
            {t.name}{t.symbol ? ` (${t.symbol})` : ''}{!selectedNetworkId && t.network ? ` — ${t.network.name}` : ''}
          </option>
        ))}
      </select>
    );
  }

  if (field.type === 'select') {
    return (
      <select value={value || ''} onChange={(e) => onChange(e.target.value)} className={baseClass}>
        <option value="">Select...</option>
        {field.options.map((opt) => {
          const optValue = typeof opt === 'string' ? opt : opt.value;
          const optLabel = typeof opt === 'string' ? opt : opt.label;
          return (
            <option key={optValue} value={optValue}>
              {optLabel}
            </option>
          );
        })}
      </select>
    );
  }

  if (field.type === 'textarea') {
    return (
      <div>
        {supportsVariables && (
          <div className="flex justify-end mb-1">
            <VariablePicker onInsert={handleInsertVariable} />
          </div>
        )}
        <textarea
          ref={inputRef}
          value={value || ''}
          onChange={(e) => onChange(e.target.value)}
          placeholder={field.placeholder}
          rows={3}
          className={baseClass + ' resize-y font-mono text-xs'}
        />
      </div>
    );
  }

  return (
    <div>
      {supportsVariables && (
        <div className="flex justify-end mb-1">
          <VariablePicker onInsert={handleInsertVariable} />
        </div>
      )}
      <input
        ref={inputRef}
        type={field.type === 'password' ? 'password' : 'text'}
        value={value || ''}
        onChange={(e) => onChange(e.target.value)}
        placeholder={field.placeholder}
        className={baseClass + (supportsVariables ? ' font-mono text-xs' : '')}
      />
    </div>
  );
}
